import { Form, Row } from 'antd';
import { capitalize } from 'lodash';
import moment from 'moment-timezone';
import queryString from 'querystring';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { handleApiErrors } from '../../../api/axiosInstance';
import { RECEIPT_API } from '../../../api/receipt';
import { Button, CustomTable } from '../../../components';
import ReceiptEditModal from '../../../components/Modal/ReceiptEditModal';
import CommonTableFilters from '../../../components/shared-ui/CommonTableFilters';
import ProfileNamePicture from '../../../components/shared-ui/ProfileNamePicture';
import StatusTag from '../../../components/Tag/StatusTag';
import Toast from '../../../components/Toast';
import { STATUS_LIST } from '../../../enum';
import usePaginatedFiltersQuery from '../../../hooks/queries/usePaginatedFiltersQuery';
import useTableSort from '../../../hooks/useTableSort';
import { momentFormat } from '../../../utils/common';
import { getMomentDateWithoutTimezone } from '../../../utils/datetime';
import { formatNumberWithCurrency, getMinutesAsMilliseconds } from '../../../utils/numbers';
import { selectStoreCurrentAuthUser } from '../../../utils/storeSelectors';

const TripReceiptModificationSection = props => {
  const { t, currentCompany } = props;
  const [form] = Form.useForm();
  const [receiptData, setReceiptData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receiptToEdit, setReceiptToEdit] = useState();
  const handleCloseModal = () => setReceiptToEdit();
  const authUser = useSelector(selectStoreCurrentAuthUser);

  const { page, pageSize, user, startDateRange, endDateRange } = queryString.parse(
    props.location.search.replace('?', ''),
  );

  const { stringTableSort, handleTableSort } = useTableSort({ seqId: -1 });
  const [filters, setFilters] = useState({
    userId: user || null,
    dateRange: [
      startDateRange ? moment(startDateRange) : moment().subtract(30, 'days').startOf('day'),
      endDateRange ? moment(endDateRange) : moment(),
    ],
  });

  const receiptsQueryParams = useMemo(() => {
    return {
      fromDate:
        Array.isArray(filters.dateRange) && filters.dateRange[0]
          ? momentFormat(filters.dateRange[0], 'YYYY-MM-DD')
          : '',
      toDate:
        Array.isArray(filters.dateRange) && filters.dateRange[1]
          ? momentFormat(filters.dateRange[1], 'YYYY-MM-DD')
          : '',
      userId: filters.userId,
      companyId: currentCompany._id,
      type: 'fixed',
      status: JSON.stringify(['pending', 'overdue']),
      dateRangeMode: 'dateRange',
    };
    // eslint-disable-next-line
  }, [filters]);

  const {
    query: receiptsQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery(
    {
      staleTime: getMinutesAsMilliseconds(5),
      placeholderData: {
        data: [],
        totalCount: 0,
      },
      enabled: !!currentCompany._id,
      queryKey: ['fetchTripReceipts', ...Object.values(receiptsQueryParams), stringTableSort],
      queryFn: () =>
        RECEIPT_API.fetchTripReceipts(
          {
            ...receiptsQueryParams,
            sort: stringTableSort,
          },
          paginationConfig.current,
          paginationConfig.pageSize,
        ),
      onSuccess: response => {
        let receiptData = [];
        response.data.forEach(receipt => {
          receiptData.push({ ...receipt });
        });
        setReceiptData(receiptData);
      },
      onError: error => {
        handleApiErrors(error.response, () => {
          Toast({
            type: 'error',
            message: t('fetchCompanyTripReceiptsError'),
          });
        });
      },
    },
    {
      resetPageQueryKey: Object.values(receiptsQueryParams),
      useQueryParams: true,
      initialPage: page,
      initialPageSize: pageSize,
    },
  );

  const handleUpdateReceipt = async values => {
    setIsSubmitting(true);

    try {
      await RECEIPT_API.updateReceipt(receiptToEdit._id, values);
      handleCloseModal();
      receiptsQuery.refetch();
      Toast({
        type: 'open',
        message: t('receiptUpdateSuccess'),
      });
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorUpdatingReceipt'),
        });
      });
    }

    setIsSubmitting(false);
  };

  const COLUMNS = [
    {
      width: 50,
      title: t('ID'),
      defaultSortOrder: 'descend',
      dataIndex: 'seqId',
      key: 'seqId',
      sorter: true,
    },
    {
      width: 125,
      title: t('name'),
      key: ['submittedBy.lastName', 'submittedBy.firstName'].toString(),
      sorter: true,
      render: (actions, data) => <ProfileNamePicture t={t} user={data.submittedBy} />,
    },
    {
      width: 75,
      title: t('rbId'),
      key: 'reimbursementId',
      dataIndex: 'reimbursementId',
      sorter: true,
    },
    {
      width: 100,
      title: t('reimbursementPeriod'),
      dataIndex: 'startOfPeriod	',
      key: 'startOfPeriod	',
      render: startOfPeriod => {
        return getMomentDateWithoutTimezone(startOfPeriod).format('MMMM YYYY');
      },
    },
    {
      width: 100,
      align: 'right',
      title: t('totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      sorter: true,
      render: (actions, data) => {
        const adjustment = data.adjustment ? data.adjustment.amount : 0;

        return formatNumberWithCurrency(
          parseFloat(adjustment) + parseFloat(data.totalAmount),
          data.currency,
          2,
        );
      },
    },
    {
      width: 140,
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (actions, data) => (
        <StatusTag
          status={
            data.status === STATUS_LIST().Status.PENDING
              ? STATUS_LIST().Status.AWAITING_APPROVAL
              : data.status
          }
        />
      ),
      sorter: true,
    },
    {
      width: 100,
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      render: type => capitalize(type),
      sorter: true,
    },
    {
      width: 100,
      title: 'Actions',
      key: 'actions',
      render: receipt => {
        return (
          <Row>
            <Button
              size="sm"
              type="primary"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setReceiptToEdit(receipt);
              }}
            >
              {t('Update')}
            </Button>
          </Row>
        );
      },
    },
  ];

  const updateFilters = (filters = {}, callback) => {
    setFilters(state => ({ ...state, ...filters }), callback);
  };

  return (
    <section>
      <CommonTableFilters
        hiddenFilters={['group', 'type', 'crmMatch', 'search', 'status']}
        loggedInUserProfile={authUser}
        disabledFilters={receiptsQuery.isFetching}
        dateRangeValue={filters.dateRange}
        onDateRangeChange={dateRange => updateFilters({ dateRange })}
        userValue={filters.userId}
        onUserChange={userId => updateFilters({ userId })}
      />

      <CustomTable
        asyncSort
        showSearchInput={false}
        loading={receiptsQuery.isFetching}
        dataSource={receiptData}
        rowKey={data => data._id}
        columns={COLUMNS}
        scroll={{ x: 970 }}
        onChange={({ current }, _, sorters) => {
          handlePageChange(current);
          handleTableSort(sorters?.columnKey, sorters?.order);
        }}
        pagination={{
          pageSize: paginationConfig.pageSize,
          total: paginationConfig.total,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
      />

      <ReceiptEditModal
        t={t}
        isSubmitting={isSubmitting}
        visible={!!receiptToEdit}
        formInstance={form}
        initialValues={receiptToEdit}
        onCancel={handleCloseModal}
        onSubmit={() => {
          form.validateFields().then(values => {
            handleUpdateReceipt(values);
          });
        }}
      />
    </section>
  );
};

export default TripReceiptModificationSection;
